import { Alert, CircularProgress } from "@mui/material";
import * as React from "react";

import { TimeRange } from "@/shared/domain/dashboards/TimeRange";
import {
  ChartDisplayType,
  NumericAggregation,
  Period,
} from "@/shared/domain/metrics";
import {
  useAggregateMetrics,
  useInfiniteQueryMetrics,
} from "@/shared/domain/metrics/hooks";
import { nsToDateLabel } from "@/shared/time/time";
import type { Condition } from "@/types";

import { aggregateRequestFor } from "./aggregateRequest";
import { type BucketWindow } from "./bucketWindow";
import { fillMissingBuckets } from "./fillMissingBuckets";
import styles from "./MetricChart.module.css";
import { MetricChartView, type PlotDatum } from "./MetricChartView";
import { toRawSeries } from "./rawSeries";
import { rawRequestFor } from "./rawSeriesRequest";

export interface MetricChartProps {
  metricName: string;
  orgId: string | undefined;
  period: Period;
  aggregation: NumericAggregation;
  /** The resolved window to aggregate over. A preset and a custom range
   *  arrive identically — the card never learns which it was. */
  range: TimeRange;
  /** Optional: restrict to specific device(s). */
  deviceIds?: string[];
  /** Optional: restrict to specific session(s). */
  sessionIds?: string[];
  /** Optional: session-property / custom-field predicate applied server-side. */
  condition?: Condition;
  /** Line (default) or bar rendering. */
  display?: ChartDisplayType;
  /** Fired when a real (non-placeholder) bucket is clicked. */
  onBucketClick?: (bucket: BucketWindow) => void;
}

/**
 * Data wrapper for {@link MetricChartView}: fetches aggregate metrics, handles
 * loading/error states, gap-fills the series, and maps records to the
 * pre-formatted {@link PlotDatum}[] the pure view renders.
 */
export function MetricChart({
  metricName,
  orgId,
  period,
  aggregation,
  range,
  deviceIds,
  sessionIds,
  condition,
  display = "line",
  onBucketClick,
}: MetricChartProps) {
  const request = aggregateRequestFor({
    metricName,
    period,
    aggregation,
    range,
    deviceIds,
    sessionIds,
    condition,
  });

  const { data, isLoading, error } = useAggregateMetrics(request, orgId);

  const series: PlotDatum[] = React.useMemo(() => {
    if (!data) {
      return [];
    }
    // Carry the bigint ns bounds through unchanged — only the display strings
    // need a (lossy, but date-resolution-irrelevant) `number` via nsToDateLabel.
    const records = data.records.map((r) => ({
      start_time: r.start_time,
      end_time: r.end_time,
      value: r.value,
    }));
    const filled = fillMissingBuckets(records);
    return filled.map((b) => ({
      start_time: b.start_time,
      end_time: b.end_time,
      value: "value" in b ? (b as { value: number }).value : undefined,
      label: nsToDateLabel(Number(b.start_time)),
      tooltipTitle: `${nsToDateLabel(Number(b.start_time))} - ${nsToDateLabel(Number(b.end_time))}`,
    }));
  }, [data]);

  if (isLoading) {
    return (
      <div className={styles.state}>
        <CircularProgress size={24} />
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load metric data: {error.message}
      </Alert>
    );
  }

  return (
    <MetricChartView
      series={series}
      metricName={metricName}
      // Every bucket carries the same definition-resolved unit, so the first
      // record is representative.
      unit={data?.records[0]?.unit ?? null}
      display={display}
      onDatumClick={(datum) =>
        onBucketClick?.({
          start_time: datum.start_time,
          end_time: datum.end_time,
        })
      }
    />
  );
}

export interface RawMetricChartProps {
  metricName: string;
  orgId: string | undefined;
  /** Inclusive lower bound, Unix-epoch nanoseconds. */
  startTimeNs: number;
  /** Exclusive upper bound, Unix-epoch nanoseconds. */
  endTimeNs: number;
  /** Optional: restrict to specific device(s). */
  deviceIds?: string[];
  /** Optional: restrict to specific session(s). */
  sessionIds?: string[];
  /** Optional: session-property / custom-field predicate applied server-side. */
  condition?: Condition;
  /** Fired with the clicked point. A raw point is one record at one instant,
   *  so what the caller drills into is that instant — see `instantWindow`. */
  onPointClick?: (datum: PlotDatum) => void;
}

/**
 * Data wrapper for {@link MetricChartView} in RAW mode: the metric's own
 * per-session values over the window, plotted unbucketed on a
 * time-proportional x-axis.
 *
 * Runs the SAME `rawRequestFor(...)` query as the card around it, so the two
 * share one React Query cache entry and one fetch — which is what lets the
 * card's "Load all" control drain pages that land in the series drawn here.
 *
 * Deliberately does NOT gap-fill: {@link fillMissingBuckets} exists to keep an
 * evenly-spaced category axis time-proportional, and there are no uniform
 * buckets here to synthesize from. The linear axis draws the real gaps
 * instead.
 */
export function RawMetricChart({
  metricName,
  orgId,
  startTimeNs,
  endTimeNs,
  deviceIds,
  sessionIds,
  condition,
  onPointClick,
}: RawMetricChartProps) {
  const request = rawRequestFor({
    metricName,
    startTimeNs,
    endTimeNs,
    deviceIds,
    sessionIds,
    condition,
  });

  const { data, isLoading, error } = useInfiniteQueryMetrics(request, orgId);

  const series: PlotDatum[] = React.useMemo(
    () => toRawSeries(data?.pages.flatMap((p) => p.items) ?? []).points,
    [data],
  );

  // Every record under one metric resolves the same definition-level unit, so
  // the first row of the first page is representative — the same reasoning the
  // aggregated chart applies to its first bucket.
  const unit = data?.pages[0]?.items[0]?.unit ?? null;

  if (isLoading) {
    return (
      <div className={styles.state}>
        <CircularProgress size={24} />
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load metric data: {error.message}
      </Alert>
    );
  }

  return (
    <MetricChartView
      series={series}
      metricName={metricName}
      unit={unit}
      xScale="linear"
      onDatumClick={(datum) => onPointClick?.(datum)}
    />
  );
}
